/**
 * Media Device Diagnostics
 * 
 * Logs all available cameras, microphones, and speakers on the device
 * Tracks which device is actively being used
 * Reports real-time video/audio stream statistics
 */

export interface MediaDeviceInfo {
  deviceId: string
  groupId: string
  kind: 'videoinput' | 'audioinput' | 'audiooutput'
  label: string
}

export interface ActiveMediaDevice {
  deviceId: string
  label: string
  kind: 'videoinput' | 'audioinput'
  settings: MediaTrackSettings
  capabilities: MediaTrackCapabilities
}

export interface VideoStreamStats {
  width: number
  height: number
  frameRate: number
  bitrate?: number
  codec?: string
  deviceId?: string
  deviceLabel?: string
}

export interface AudioStreamStats {
  sampleRate: number
  channelCount: number
  bitrate?: number
  codec?: string
  deviceId?: string
  deviceLabel?: string
}

export interface MediaDiagnostics {
  timestamp: string
  deviceInfo: {
    totalCameras: number
    totalMicrophones: number
    totalSpeakers: number
    cameras: MediaDeviceInfo[]
    microphones: MediaDeviceInfo[]
    speakers: MediaDeviceInfo[]
  }
  activeDevices: {
    camera?: ActiveMediaDevice
    microphone?: ActiveMediaDevice
  }
  streamStats: {
    video?: VideoStreamStats
    audio?: AudioStreamStats
  }
}

/**
 * Enumerate all media devices on the system
 */
export async function enumerateMediaDevices(): Promise<{
  cameras: MediaDeviceInfo[]
  microphones: MediaDeviceInfo[]
  speakers: MediaDeviceInfo[]
}> {
  try {
    // Request permissions first to get device labels
    await navigator.mediaDevices.getUserMedia({ video: true, audio: true })
      .then(stream => {
        // Stop tracks immediately - we just needed permissions
        stream.getTracks().forEach(track => track.stop())
      })
      .catch(() => {
        // Permissions denied - labels will be empty
      })

    const devices = await navigator.mediaDevices.enumerateDevices()
    
    const cameras: MediaDeviceInfo[] = []
    const microphones: MediaDeviceInfo[] = []
    const speakers: MediaDeviceInfo[] = []

    devices.forEach(device => {
      const info: MediaDeviceInfo = {
        deviceId: device.deviceId,
        groupId: device.groupId,
        kind: device.kind as any,
        label: device.label || `${device.kind} (no permission)`,
      }

      if (device.kind === 'videoinput') {
        cameras.push(info)
      } else if (device.kind === 'audioinput') {
        microphones.push(info)
      } else if (device.kind === 'audiooutput') {
        speakers.push(info)
      }
    })

    return { cameras, microphones, speakers }
  } catch (error) {
    console.error('[MediaDiagnostics] Failed to enumerate devices:', error)
    return { cameras: [], microphones: [], speakers: [] }
  }
}

/**
 * Get active device info from a media track
 */
export function getActiveDeviceInfo(track: MediaStreamTrack): ActiveMediaDevice | null {
  try {
    const settings = track.getSettings()
    const capabilities = track.getCapabilities()

    return {
      deviceId: settings.deviceId || 'unknown',
      label: track.label || 'Unknown device',
      kind: track.kind as 'videoinput' | 'audioinput',
      settings: settings,
      capabilities: capabilities,
    }
  } catch (error) {
    console.error('[MediaDiagnostics] Failed to get active device info:', error)
    return null
  }
}

/**
 * Get video stream statistics
 */
export function getVideoStreamStats(track: MediaStreamTrack): VideoStreamStats | null {
  if (track.kind !== 'video') return null

  try {
    const settings = track.getSettings()
    
    return {
      width: settings.width || 0,
      height: settings.height || 0,
      frameRate: settings.frameRate || 0,
      deviceId: settings.deviceId,
      deviceLabel: track.label,
    }
  } catch (error) {
    console.error('[MediaDiagnostics] Failed to get video stats:', error)
    return null
  }
}

/**
 * Get audio stream statistics
 */
export function getAudioStreamStats(track: MediaStreamTrack): AudioStreamStats | null {
  if (track.kind !== 'audio') return null

  try {
    const settings = track.getSettings()
    
    return {
      sampleRate: settings.sampleRate || 0,
      channelCount: settings.channelCount || 0,
      deviceId: settings.deviceId,
      deviceLabel: track.label,
    }
  } catch (error) {
    console.error('[MediaDiagnostics] Failed to get audio stats:', error)
    return null
  }
}

/**
 * Get comprehensive media diagnostics
 */
export async function getMediaDiagnostics(
  videoTrack?: MediaStreamTrack,
  audioTrack?: MediaStreamTrack
): Promise<MediaDiagnostics> {
  const devices = await enumerateMediaDevices()
  
  const activeCamera = videoTrack ? getActiveDeviceInfo(videoTrack) : undefined
  const activeMicrophone = audioTrack ? getActiveDeviceInfo(audioTrack) : undefined
  
  const videoStats = videoTrack ? getVideoStreamStats(videoTrack) : undefined
  const audioStats = audioTrack ? getAudioStreamStats(audioTrack) : undefined

  return {
    timestamp: new Date().toISOString(),
    deviceInfo: {
      totalCameras: devices.cameras.length,
      totalMicrophones: devices.microphones.length,
      totalSpeakers: devices.speakers.length,
      cameras: devices.cameras,
      microphones: devices.microphones,
      speakers: devices.speakers,
    },
    activeDevices: {
      camera: activeCamera ?? undefined,
      microphone: activeMicrophone ?? undefined,
    },
    streamStats: {
      video: videoStats ?? undefined,
      audio: audioStats ?? undefined,
    },
  }
}

/**
 * Format diagnostics for console logging
 */
export function formatMediaDiagnostics(diagnostics: MediaDiagnostics): string {
  const lines = [
    '',
    '╔══════════════════════════════════════════════════════════════╗',
    '║                  📹 MEDIA DEVICE DIAGNOSTICS                 ║',
    '╠══════════════════════════════════════════════════════════════╣',
    '║ AVAILABLE DEVICES:                                           ║',
    `║   📹 Cameras: ${diagnostics.deviceInfo.totalCameras.toString().padEnd(49)}║`,
  ]

  // List all cameras
  diagnostics.deviceInfo.cameras.forEach((cam, idx) => {
    const label = cam.label.length > 45 ? cam.label.substring(0, 42) + '...' : cam.label
    const isActive = diagnostics.activeDevices.camera?.deviceId === cam.deviceId ? ' ✓ ACTIVE' : ''
    lines.push(`║     ${(idx + 1)}. ${(label + isActive).padEnd(54)}║`)
  })

  lines.push(`║   🎤 Microphones: ${diagnostics.deviceInfo.totalMicrophones.toString().padEnd(44)}║`)
  
  // List all microphones
  diagnostics.deviceInfo.microphones.forEach((mic, idx) => {
    const label = mic.label.length > 45 ? mic.label.substring(0, 42) + '...' : mic.label
    const isActive = diagnostics.activeDevices.microphone?.deviceId === mic.deviceId ? ' ✓ ACTIVE' : ''
    lines.push(`║     ${(idx + 1)}. ${(label + isActive).padEnd(54)}║`)
  })

  lines.push(`║   🔊 Speakers: ${diagnostics.deviceInfo.totalSpeakers.toString().padEnd(47)}║`)

  // Active camera details
  if (diagnostics.activeDevices.camera && diagnostics.streamStats.video) {
    lines.push('╠══════════════════════════════════════════════════════════════╣')
    lines.push('║ ACTIVE CAMERA:                                               ║')
    lines.push(`║   Device: ${diagnostics.activeDevices.camera.label.substring(0, 49).padEnd(49)}║`)
    lines.push(`║   Resolution: ${`${diagnostics.streamStats.video.width}x${diagnostics.streamStats.video.height}`.padEnd(45)}║`)
    lines.push(`║   Frame Rate: ${`${diagnostics.streamStats.video.frameRate} fps`.padEnd(45)}║`)
    lines.push(`║   Device ID: ${diagnostics.activeDevices.camera.deviceId.substring(0, 46).padEnd(46)}║`)
  }

  // Active microphone details
  if (diagnostics.activeDevices.microphone && diagnostics.streamStats.audio) {
    lines.push('╠══════════════════════════════════════════════════════════════╣')
    lines.push('║ ACTIVE MICROPHONE:                                           ║')
    lines.push(`║   Device: ${diagnostics.activeDevices.microphone.label.substring(0, 49).padEnd(49)}║`)
    lines.push(`║   Sample Rate: ${`${diagnostics.streamStats.audio.sampleRate} Hz`.padEnd(43)}║`)
    lines.push(`║   Channels: ${diagnostics.streamStats.audio.channelCount.toString().padEnd(48)}║`)
  }

  lines.push('╚══════════════════════════════════════════════════════════════╝')
  lines.push('')

  return lines.join('\n')
}

/**
 * Log media diagnostics to console
 */
export function logMediaDiagnostics(diagnostics: MediaDiagnostics): void {
  console.log('[MediaDiagnostics] DEVICE AND STREAM DIAGNOSTICS', {
    timestamp: diagnostics.timestamp,
    devices: {
      cameras: diagnostics.deviceInfo.cameras.map(c => ({
        label: c.label,
        id: c.deviceId,
        active: diagnostics.activeDevices.camera?.deviceId === c.deviceId,
      })),
      microphones: diagnostics.deviceInfo.microphones.map(m => ({
        label: m.label,
        id: m.deviceId,
        active: diagnostics.activeDevices.microphone?.deviceId === m.deviceId,
      })),
    },
    activeCamera: diagnostics.activeDevices.camera ? {
      label: diagnostics.activeDevices.camera.label,
      deviceId: diagnostics.activeDevices.camera.deviceId,
      resolution: diagnostics.streamStats.video ? 
        `${diagnostics.streamStats.video.width}x${diagnostics.streamStats.video.height}` : 'Unknown',
      frameRate: diagnostics.streamStats.video?.frameRate || 'Unknown',
    } : null,
    activeMicrophone: diagnostics.activeDevices.microphone ? {
      label: diagnostics.activeDevices.microphone.label,
      deviceId: diagnostics.activeDevices.microphone.deviceId,
      sampleRate: diagnostics.streamStats.audio?.sampleRate || 'Unknown',
    } : null,
  })

  // Pretty formatted box
  console.log(formatMediaDiagnostics(diagnostics))
}
